import React, { useState } from 'react';
import styles from './SignupRoute.module.scss';
import g2users from 'images/signup/g2_badge_users_love_us.svg';
import g2leader from 'images/signup/g2_badge_momentum_leader.svg';
import g2leadership from 'images/signup/g2_badge_leader.svg';
import { Button, LinkButton } from '@timelyapp/react';
import config from 'config';
import googleLogo from 'images/apps_logo/google.svg';
import appleLogo from 'images/apple.svg';
import Input from 'components/Input';
import InputLabel from 'components/InputLabel';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { InputValidation } from 'components/InputValidation';
import { TimelyIcon } from 'components/Icons';

const errorHandler = (errors: Record<'email' | 'name' | 'password', Array<string>>) => {
  const { email = [], name = [], password = [] } = errors;

  return {
    email: email[0] ? email[0].replace(/&#39;/g, "'") : undefined,
    name: name[0] ? name[0].replace(/&#39;/g, "'") : undefined,
    password: password[0] ? password[0].replace(/&#39;/g, "'") : undefined,
  };
};

const SignupRoute: React.FC = () => {
  const [nameInput, setNameInput] = useState('');
  const [emailInput, setEmailInput] = useState('');
  const [passwordInput, setPasswordInput] = useState('');
  const { email, name, password } = errorHandler(
    window.BOOTSTRAPPED_DATA?.registration_form_errors
  );

  return (
    <>
      <header>
        <TimelyIcon className={styles.app_icon} />
        <h1>
          Sign up to Timely <br /> No credit card required
        </h1>
      </header>
      <main>
        <div className={styles.testimonialContainer}>
          <h2>Top rated time tracking</h2>
          <div className={styles.g2Container}>
            <img
              alt="Timely is a leader in Time Tracking on G2"
              src={g2leader}
              width="116"
              height="150"
            />
            <img
              alt="Timely is a leader in Most Implementable Time Tracking on G2"
              src={g2users}
              width="116"
              height="150"
            />
            <img
              alt="Timely is a leader in Time Tracking with Best Usability on G2"
              src={g2leadership}
              width="116"
              height="150"
            />
          </div>
          <div className={styles.testimonialItems}>
            <h3>“The best way to measure exactly where your time goes.”</h3>
            <div>
              <span>Brian,</span>
              Accountant
            </div>
          </div>
          <div className={styles.testimonialItems}>
            <h3>“Timely takes a load off my brain.”</h3>
            <div>
              <span>Erik,</span>
              Digital Designer
            </div>
          </div>
          <div className={styles.testimonialItems}>
            <h3>“Easy to use and I love the project level summaries.”</h3>
            <div>
              <span>Jonathan,</span>
              Software Engineer
            </div>
          </div>
        </div>
        <div className={styles.formContainer}>
          <div className={styles.inner}>
            <ul className={styles.options}>
              {window.BOOTSTRAPPED_DATA.google_sso_url && (
                <li>
                  <LinkButton
                    priority="low"
                    size="large"
                    linkTo={window.BOOTSTRAPPED_DATA.google_sso_url}
                    className={styles.ssoButton}
                    id="google-login-link"
                    data-testid="google_signup_button"
                  >
                    <img
                      src={googleLogo}
                      width={36}
                      height={40}
                      alt="Google logo"
                      role="presentation"
                    />
                    Sign up with Google
                  </LinkButton>
                </li>
              )}
              {window.BOOTSTRAPPED_DATA.apple_sso_url && (
                <li>
                  <LinkButton
                    priority="low"
                    size="large"
                    linkTo={window.BOOTSTRAPPED_DATA.apple_sso_url}
                    className={styles.ssoButton}
                    id="apple-login-link"
                    data-testid="apple_signup_button"
                  >
                    <img
                      src={appleLogo}
                      width={15}
                      height={19}
                      className={styles.appleLogo}
                      alt="Apple logo"
                      role="presentation"
                    />
                    Sign up with Apple
                  </LinkButton>
                </li>
              )}
            </ul>
            <div className={styles.details}>
              <span />
              <p>or enter details</p>
              <span />
            </div>
            <div className={styles.signupForm}>
              <form
                id="new_registration_form"
                action="/registration"
                method="post"
                data-hs-cf-bound="true"
              >
                <input
                  type="hidden"
                  name="authenticity_token"
                  value={window.BOOTSTRAPPED_DATA.authencity_token}
                />
                <div className={styles.inputContainer}>
                  <InputValidation style={{ margin: '0 0 10px' }} validationError={email}>
                    <Input
                      className="fs-mask"
                      type="email"
                      name="registration_form[email]"
                      value={emailInput}
                      onChange={event => setEmailInput(event.currentTarget.value)}
                      data-testid="email_input"
                    />
                  </InputValidation>
                  <InputLabel className={classNames({ [styles.activeLabel]: emailInput })}>
                    Work email
                  </InputLabel>
                </div>
                <div className={styles.inputContainer}>
                  <InputValidation style={{ margin: '0 0 10px' }} validationError={name}>
                    <Input
                      value={nameInput}
                      onChange={event => setNameInput(event.currentTarget.value)}
                      className="fs-mask"
                      type="text"
                      name="registration_form[name]"
                      data-testid="name_input"
                    />
                  </InputValidation>
                  <InputLabel className={classNames({ [styles.activeLabel]: nameInput })}>
                    Full name
                  </InputLabel>
                </div>
                <div className={styles.inputContainer}>
                  <InputValidation style={{ margin: '0 0 10px' }} validationError={password}>
                    <Input
                      className="fs-mask"
                      type="password"
                      name="registration_form[password]"
                      autoComplete="new-password"
                      value={passwordInput}
                      onChange={event => setPasswordInput(event.currentTarget.value)}
                      data-testid="password_input"
                    />
                  </InputValidation>
                  <InputLabel className={classNames({ [styles.activeLabel]: passwordInput })}>
                    Password
                  </InputLabel>
                </div>
                <div className={styles.tos}>
                  By signing up you agree to our{' '}
                  <a
                    href={config.termsOfServiceUrl}
                    target="_blank"
                    rel="noopener"
                    data-testid="terms_of_service_link"
                  >
                    Terms of Service
                  </a>
                </div>
                <Button
                  data-auth="memory"
                  type="submit"
                  className={styles.trialButton}
                  priority="xhigh"
                  size="large"
                  data-testid="start_free_trial_button"
                >
                  Start 14-day free trial
                </Button>
              </form>
            </div>
          </div>

          <div className={styles.login}>
            Already have an account? &nbsp;{' '}
            <a
              href={`${window.BOOTSTRAPPED_DATA.host}/login`}
              target="_blank"
              rel="noopener"
              data-testid="log_in_link"
            >
              Log in
            </a>
          </div>
        </div>
      </main>
      <footer className={styles.footer}>Copyright {DateTime.now().year} Timely AS</footer>
    </>
  );
};

export default SignupRoute;
